import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatIDR, periodLabel } from "@/lib/format";

const DETAIL_CATS = [
  {
    key: "bahan_baku", label: "Bahan Baku",
    columns: [
      { key: "tanggal", label: "Tanggal", w: 22 },
      { key: "item", label: "Item Barang" },
      { key: "jumlah", label: "Jumlah", w: 16, align: "right" },
      { key: "anggaran", label: "Penjualan", money: true, w: 28, align: "right" },
      { key: "pembelian", label: "Pembelian", money: true, w: 28, align: "right" },
      { key: "selisih", label: "Selisih", money: true, w: 28, align: "right",
        compute: (r) => Number(r.anggaran || 0) - Number(r.pembelian || 0) },
    ],
  },
  {
    key: "ops", label: "Operasional",
    columns: [
      { key: "tanggal", label: "Tanggal", w: 22 },
      { key: "item", label: "Item" },
      { key: "jumlah", label: "Jumlah", w: 16, align: "right" },
      { key: "anggaran", label: "Penjualan", money: true, w: 28, align: "right" },
      { key: "pembelian", label: "Pembelian", money: true, w: 28, align: "right" },
      { key: "selisih", label: "Selisih", money: true, w: 28, align: "right",
        compute: (r) => Number(r.anggaran || 0) - Number(r.pembelian || 0) },
    ],
  },
  {
    key: "insentif", label: "Insentif Usaha",
    columns: [
      { key: "tanggal", label: "Tanggal", w: 24 },
      { key: "nama", label: "Nama" },
      { key: "anggaran", label: "Penjualan", money: true, w: 30, align: "right" },
      { key: "realisasi", label: "Realisasi", money: true, w: 30, align: "right" },
      { key: "selisih", label: "Selisih", money: true, w: 30, align: "right",
        compute: (r) => Number(r.anggaran || 0) - Number(r.realisasi || 0) },
    ],
  },
  {
    key: "lainnya", label: "Lainnya",
    columns: [
      { key: "tanggal", label: "Tanggal", w: 26 },
      { key: "item", label: "Item" },
      { key: "pembelian", label: "Pembelian", money: true, w: 36, align: "right" },
      { key: "catatan", label: "Catatan" },
    ],
  },
];

/**
 * Generate laporan PDF A4 vertical dari data dashboard/summary + dashboard/detail.
 * @param {Object} opts
 * @param {string} opts.usahaLabel
 * @param {string} opts.cabangLabel
 * @param {'daily'|'weekly'|'monthly'|'yearly'} opts.periodType
 * @param {string} opts.periodRangeLabel
 * @param {Object} opts.data  Response dari /api/dashboard/summary
 * @param {Object} [opts.detail]  Response dari /api/dashboard/detail (bahan_baku/ops/insentif/lainnya arrays)
 * @param {string} [opts.filename]
 */
export function generateReportPDF({ usahaLabel, cabangLabel, periodType, periodRangeLabel, data, detail, filename }) {
  const t = data.totals;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // Header
  doc.setFillColor(16, 185, 129);
  doc.rect(0, 0, pageW, 22, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16); doc.setFont("helvetica", "bold");
  doc.text("Laporan Keuangan", 14, 10);
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  doc.text("Kelola Usaha — Manajemen Keuangan Multi-Cabang", 14, 16);

  // Metadata
  doc.setTextColor(30, 41, 59);
  doc.setFontSize(10);
  let y = 30;
  [
    ["Usaha", usahaLabel],
    ["Cabang", cabangLabel],
    ["Periode", `${periodLabel(periodType)} · ${periodRangeLabel}`],
    ["Dibuat", new Date().toLocaleString("id-ID")],
  ].forEach(([k, v]) => {
    doc.setFont("helvetica", "bold"); doc.text(`${k}:`, 14, y);
    doc.setFont("helvetica", "normal"); doc.text(String(v), 44, y);
    y += 6;
  });

  // Ringkasan — Laba Kotor hanya dari BB + Insentif (agar Ops & Lainnya tidak double-count di Pengeluaran)
  const pendapatanDisplay = Number(t.selisih_bahan_baku || 0) + Number(t.selisih_insentif || 0);
  const pengeluaranDisplay = Number(t.ops || 0) + Number(t.lainnya || 0);
  const labaDisplay = pendapatanDisplay - pengeluaranDisplay;

  y += 4;
  doc.setFontSize(12); doc.setFont("helvetica", "bold");
  doc.text("Ringkasan", 14, y);
  autoTable(doc, {
    startY: y + 3,
    head: [["Metrik", "Nilai"]],
    body: [
      ["Total Laba Kotor", formatIDR(pendapatanDisplay)],
      ["  Selisih Bahan Baku", formatIDR(t.selisih_bahan_baku)],
      ["  Selisih Insentif Usaha", formatIDR(t.selisih_insentif)],
      ["Total Pengeluaran", formatIDR(pengeluaranDisplay)],
      ["  Operasional (Pembelian)", formatIDR(t.ops)],
      ["  Lainnya (Pembelian)", formatIDR(t.lainnya)],
      ["Laba / Rugi", formatIDR(labaDisplay)],
    ],
    headStyles: { fillColor: [16, 185, 129], textColor: 255 },
    styles: { fontSize: 10, cellPadding: 2.5 },
    columnStyles: { 1: { halign: "right", fontStyle: "bold" } },
    margin: { left: 14, right: 14 },
  });

  // Detail per Kategori
  if (detail) {
    DETAIL_CATS.forEach((cat) => {
      const rows = detail[cat.key] || [];
      if (rows.length === 0) return;
      const startY = doc.lastAutoTable.finalY + 8;
      // Judul + pindah halaman kalau mepet
      if (startY > pageH - 40) doc.addPage();
      const yHead = startY > pageH - 40 ? 20 : startY;
      doc.setFontSize(12); doc.setFont("helvetica", "bold"); doc.setTextColor(30, 41, 59);
      doc.text(`Rincian ${cat.label}`, 14, yHead);
      // Sort rows by tanggal
      const sorted = [...rows].sort((a, b) => String(a.tanggal || "").localeCompare(String(b.tanggal || "")));
      const body = sorted.map((r) => cat.columns.map((c) => {
        const raw = c.compute ? c.compute(r) : r[c.key];
        if (c.money) return formatIDR(raw);
        return raw ?? "";
      }));
      // Total row per kategori
      const totalRow = cat.columns.map((c) => {
        if (c.key === "tanggal") return "TOTAL";
        if (c.money) {
          const sum = sorted.reduce((s, r) => s + Number(c.compute ? c.compute(r) : r[c.key] || 0), 0);
          return formatIDR(sum);
        }
        return "";
      });
      autoTable(doc, {
        startY: yHead + 3,
        head: [cat.columns.map((c) => c.label)],
        body: [...body, totalRow],
        headStyles: { fillColor: [51, 65, 85], textColor: 255 },
        styles: { fontSize: 8.5, cellPadding: 2 },
        columnStyles: cat.columns.reduce((acc, c, i) => {
          const s = {};
          if (c.align) s.halign = c.align;
          if (c.w) s.cellWidth = c.w;
          acc[i] = s;
          return acc;
        }, {}),
        didParseCell: (d) => {
          if (d.row.index === body.length) {
            d.cell.styles.fillColor = [241, 245, 249];
            d.cell.styles.fontStyle = "bold";
            d.cell.styles.textColor = [15, 23, 42];
          }
        },
        margin: { left: 14, right: 14 },
      });
    });
  }

  // Footer
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(8); doc.setTextColor(148, 163, 184);
    doc.text(`Halaman ${i} dari ${pages}`, pageW - 14, pageH - 8, { align: "right" });
    doc.text("Kelola Usaha", 14, pageH - 8);
  }

  const fname = filename || `Laporan_${usahaLabel.replace(/\s+/g, "_")}_${cabangLabel.replace(/\s+/g, "_")}.pdf`;
  doc.save(fname);
}
